import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CHARGE_FREQUENCIES, PARKING_TYPES, EMPTY_SETTINGS_FORM } from "@/lib/parkingTypes";

export default function ParkingSettingsCard({ settings, onSaved }) {
    const [form, setForm] = useState(EMPTY_SETTINGS_FORM);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!settings) return;
        setForm({
            enabled: !!settings.enabled, charge_frequency: settings.charge_frequency || "monthly",
            default_charge: settings.default_charge ?? "", effective_from: settings.effective_from || "",
            applicable_parking_type: settings.applicable_parking_type || "", grace_period_days: settings.grace_period_days ?? "",
            late_fee: settings.late_fee ?? "", notes: settings.notes || "",
        });
    }, [settings]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSave(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.put("/parking/settings", {
                enabled: form.enabled, charge_frequency: form.charge_frequency,
                default_charge: form.default_charge !== "" ? parseFloat(form.default_charge) : null,
                effective_from: form.effective_from || null, applicable_parking_type: form.applicable_parking_type || null,
                grace_period_days: form.grace_period_days !== "" ? parseInt(form.grace_period_days, 10) : null,
                late_fee: form.late_fee !== "" ? parseFloat(form.late_fee) : null, notes: form.notes || null,
            });
            toast.success("Paid parking configuration saved");
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="bg-white border border-brand-line rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-heading text-lg text-brand-ink">Paid Parking Configuration</h3>
                    <p className="text-xs text-brand-inkSoft mt-1">Society-level defaults used to pre-fill charges when allocating a slot. Actual billing always follows each slot's own allocation.</p>
                </div>
                <Switch data-testid="settings-enabled-switch" checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} />
            </div>
            <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-2">
                        <Label>Default charge</Label>
                        <Input type="number" min="0" step="0.01" data-testid="settings-default-charge-input" value={form.default_charge} onChange={(e) => set("default_charge", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-2">
                        <Label>Frequency</Label>
                        <Select value={form.charge_frequency} onValueChange={(v) => set("charge_frequency", v)}>
                            <SelectTrigger data-testid="settings-frequency-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{CHARGE_FREQUENCIES.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Grace period (days)</Label>
                        <Input type="number" min="0" data-testid="settings-grace-input" value={form.grace_period_days} onChange={(e) => set("grace_period_days", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-2">
                        <Label>Late fee</Label>
                        <Input type="number" min="0" step="0.01" data-testid="settings-late-fee-input" value={form.late_fee} onChange={(e) => set("late_fee", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label>Effective from (optional)</Label>
                        <Input type="date" data-testid="settings-effective-from-input" value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-2">
                        <Label>Applicable parking type (optional)</Label>
                        <Select value={form.applicable_parking_type || "__any__"} onValueChange={(v) => set("applicable_parking_type", v === "__any__" ? "" : v)}>
                            <SelectTrigger data-testid="settings-parking-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__any__">Any</SelectItem>
                                {PARKING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea rows={2} data-testid="settings-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                </div>
                <div className="flex justify-end">
                    <Button type="submit" disabled={submitting} data-testid="settings-save-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                        {submitting ? "Saving…" : "Save configuration"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
